import React, { useReducer } from 'react';
import PropTypes from 'prop-types';
import { Markup } from 'interweave';
import I18n from 'utils/I18n';
import { snakeCase } from 'lodash-es';
import styles from './styles.scss';
import useFeedAnalytics from '../../hooks/useFeedAnalytics';
import {
  cursorDataProps,
  indexProps,
  pageProps,
  feedTypeProps,
  viewingAthleteProps,
  commentsProps,
  timeAndLocationProps,
  highlightedKudosersProps
} from '../../props/feed-prop-types';
import FeedEntityContext from '../shared/FeedEntityContext';
import KudosAndCommentsContext from '../../../shared/KudosCommentsAndAchievements/KudosAndCommentsContext';
import {
  feedEntityInit,
  feedEntityReducer
} from '../shared/feedEntity.reducer';
import { buildInitialFeedState } from '../shared/feedEntity.init-state';
import { buildInitialKudosAndCommentsState } from '../../../shared/KudosCommentsAndAchievements/kudosAndComments.initial-state';
import {
  initKudosAndCommentsState,
  kudosAndCommentsReducer
} from '../../../shared/KudosCommentsAndAchievements/kudosAndComments.reducer';
import CommentDeleteAlertDialog from '../../../shared/KudosCommentsAndAchievements/CommentDeleteAlertDialog';
import HeaderWithOwnerMetadata from '../shared/HeaderWithOwnerMetadata/HeaderWithOwnerMetadata';
import videoIcon from './video-icon.png';
import PhotosAndMapImage from '../shared/PhotosAndMapImage';
import KudosAndComments from '../shared/KudosAndComments';
import { getPhotoWithMaxDimension } from '../../../../utils/generalUtils';
import Card from '../shared/media/Card';
import EntryMedia from '../shared/media/EntryMedia';
import EntryBody from '../shared/media/EntryBody';
import EntryFooter from '../shared/media/EntryFooter';
import useFeedIntersectionObserver from '../../hooks/useFeedIntersectionObserver';

const Post = ({
  viewingAthlete,
  clubId,
  post,
  page,
  entity,
  feedType,
  index,
  cursorData,
  timeAndLocation,
  body,
  sharedContents,
  athleteProfileId,
  isDeletable,
  clubMentionsEnabled
}) => {
  const analyticProps = {
    page,
    entity,
    entityId: post.entity_id,
    feedType,
    index,
    athleteProfileId,
    viewingAthleteId: viewingAthlete.id,
    clubId
  };

  const { inView, intersectionObserverRef } = useFeedIntersectionObserver({
    index,
    isParentFeedEntry: true
  });

  const { trackAnalytics } = useFeedAnalytics({
    ...analyticProps,
    inView
  });

  const [feedEntityState, feedDispatch] = useReducer(
    feedEntityReducer,
    buildInitialFeedState({
      trackAnalytics,
      entity: snakeCase(entity),
      entityId: post.entity_id
    }),
    feedEntityInit
  );
  const feedProviderState = {
    feedEntityState,
    feedDispatch,
    analyticProps
  };

  const [kudosAndCommentsEntityState, dispatch] = useReducer(
    kudosAndCommentsReducer,
    buildInitialKudosAndCommentsState({
      trackAnalytics,
      comments: { [post.entity_id]: post.comments },
      entity: snakeCase(entity),
      entityId: post.entity_id
    }),
    initKudosAndCommentsState
  );
  const kudosAndCommentsProviderState = {
    kudosAndCommentsEntityState,
    dispatch,
    analyticProps
  };

  // Checks if post is authored by club or athlete
  const idForProfileUrl =
    post.originator_type === 'club' && clubId ? clubId : post.originator_id;

  // Checks if the post delongs to a club or athlete profile
  const urlPostPrefix =
    clubId && post.parent_type === 'Club' ? clubId : post.originator_id;

  const postUrl = post.path_prefix && post.path_prefix.concat(urlPostPrefix);

  const avatarUrl = () => {
    let url;
    if (post.originator_type === 'club') {
      url = post.path_prefix && post.path_prefix.concat(idForProfileUrl);
    } else {
      url = `/athletes/${post.originator_id}`;
    }
    return url;
  };

  const entityOwner = {
    displayImage: post.originator_avatar_url,
    memberType: post.member_type,
    name: post.originator_name,
    profileUrl: avatarUrl(),
    type: post.parent_type.toLowerCase()
  };

  const athleteHasDeletePermissions =
    isDeletable || viewingAthlete.id === post.originator_id.toString();

  const entryExpandedLink = () => {
    const videoThumbnailIcon = I18n.t(
      'templates.feed.posts.video_thumbnail_icon'
    );
    return sharedContents.map((content) => {
      // eg. expected scss class : shared-video is referenced as sharedVideo in JSX
      const typeClass = `shared${content.type.charAt(0).toUpperCase() +
        content.type.slice(1)}`;
      return (
        <a
          className={styles.sharedContentContainer}
          target="_blank"
          rel="noopener noreferrer"
          href={content.url}
          key={content.id}
          data-testid="shared-content"
          onClick={() =>
            trackAnalytics({
              action: 'click',
              element: 'expanded_link'
            })
          }
        >
          <div className={`${styles.sharedContent} mt-sm ${styles[typeClass]}`}>
            {/*  thumbnail image  */}
            {content.thumbnail_url && (
              <div className={styles.sharedContentImage}>
                {content.type === 'video' && (
                  <img
                    className={styles.sharedContentVideoIcon}
                    src={videoIcon}
                    alt={videoThumbnailIcon}
                  />
                )}
                <img
                  className={styles.sharedContentThumbnail}
                  src={content.thumbnail_url}
                  alt=""
                />
              </div>
            )}
            {/*  description  */}
            <div className={styles.sharedContentText}>
              <h4 className={styles.sharedContentHeader}>{content.title}</h4>
              <p className={`${styles.sharedContentDescription} mt-sm mb-sm`}>
                {content.description}
              </p>
              <p className={styles.sharedContentDomain}>{content.hostname}</p>
            </div>
          </div>
        </a>
      );
    });
  };

  const buildEntryBody = () => {
    let bodyStyle = [styles.postBody];
    if (!post.summary) {
      bodyStyle.push(styles.postBodyTitle);
    }
    bodyStyle = bodyStyle.join(' ');
    return (
      <a
        data-testid="post-details-url"
        href={`${postUrl}/posts/${post.entity_id}`}
        onClick={() =>
          trackAnalytics({
            action: 'click',
            element: 'body'
          })
        }
      >
        {post.summary && (
          <h3 className={styles.entryTitle}>
            <Markup noWrap={true} content={post.summary} />
          </h3>
        )}
        <div className={bodyStyle}>
          <Markup noWrap={true} content={body} />
        </div>
      </a>
    );
  };

  const highlightPhoto = () => {
    const photoUrlHash = post.primary_photo.urls;
    // max dimension image url
    const photoUrl = getPhotoWithMaxDimension(photoUrlHash);
    return {
      large: photoUrl,
      caption_escaped: post.primary_photo.caption,
      photo_id: post.primary_photo.unique_id
    };
  };

  return (
    <KudosAndCommentsContext.Provider value={kudosAndCommentsProviderState}>
      <FeedEntityContext.Provider value={feedProviderState}>
        <Card
          key={index}
          index={index}
          cursorData={cursorData}
          ref={intersectionObserverRef}
        >
          {/* Entry Header - avatar, location, social actions */}
          <HeaderWithOwnerMetadata
            timeAndLocation={timeAndLocation}
            entityOwner={entityOwner}
            entityId={post.entity_id.toString()}
            isBoosted={post.is_boosted || false}
            shareable={post.is_sharable || false}
            twitterUrl={post.twitter_url}
            embedDropdownEnabled={false}
            entityParentId={parseInt(idForProfileUrl, 10)}
            entityParentType={post.parent_type}
            isDeletable={athleteHasDeletePermissions}
          />
          {/* Summary, Body and Expanded Links */}

          {/* Summary & Body */}
          <EntryBody inset={true}>{buildEntryBody()}</EntryBody>

          {/* Expanded Link */}
          <EntryMedia inset={true}>{entryExpandedLink()}</EntryMedia>

          {/* Primary Photo */}
          {post.primary_photo && (
            <EntryMedia>
              <a
                href={`${postUrl}/posts/${post.entity_id}`}
                onClick={() =>
                  trackAnalytics({
                    action: 'click',
                    element: 'image'
                  })
                }
              >
                <PhotosAndMapImage
                  entryType={entity.toLowerCase()}
                  photoList={[highlightPhoto()]}
                  feedName={page}
                />
              </a>
            </EntryMedia>
          )}
          {/* Comments & Kudos */}
          <EntryFooter>
            <KudosAndComments
              kudosAndComments={{
                canKudo: post.can_kudo,
                kudosCount: post.kudos_count,
                highlightedKudosers: post.highlighted_kudosers,
                commentsEnabled: post.comments_enabled,
                comments:
                  kudosAndCommentsEntityState.comments[post.entity_id] || []
              }}
              entity={snakeCase(entity)}
              entityId={post.entity_id.toString()}
              ownedByCurrentAthlete={
                viewingAthlete.id.toString() === post.originator_id.toString()
              }
              viewingAthlete={viewingAthlete}
              entityOwner={entityOwner}
              clubMentionsEnabled={clubMentionsEnabled}
              entityTitle={post.summary || ''}
              openCommentsModalOnIconClick={false}
              showHairlineOnTop={false}
            />
          </EntryFooter>
          <CommentDeleteAlertDialog />
        </Card>
      </FeedEntityContext.Provider>
    </KudosAndCommentsContext.Provider>
  );
};

Post.propTypes = {
  // feed entry types could be Activity, group_activity, Post etc
  entity: PropTypes.oneOf(['Post']).isRequired,
  feedType: feedTypeProps.isRequired,
  page: pageProps.isRequired,
  cursorData: cursorDataProps.isRequired,
  index: indexProps.isRequired,
  viewingAthlete: viewingAthleteProps.isRequired,
  clubId: PropTypes.string.isRequired,
  post: PropTypes.shape({
    entity_id: PropTypes.number.isRequired,
    comments: commentsProps,
    originator_id: PropTypes.number.isRequired,
    originator_avatar_url: PropTypes.string,
    originator_name: PropTypes.string,
    originator_type: PropTypes.string.isRequired,
    parent_type: PropTypes.string,
    member_type: PropTypes.string,
    summary: PropTypes.string,
    is_boosted: PropTypes.bool,
    is_sharable: PropTypes.bool,
    twitter_url: PropTypes.string,
    path_prefix: PropTypes.string,
    primary_photo: PropTypes.shape({
      urls: PropTypes.shape({}),
      large: PropTypes.string,
      caption: PropTypes.string,
      unique_id: PropTypes.string
    }),
    can_kudo: PropTypes.bool,
    kudos_count: PropTypes.number,
    highlighted_kudosers: highlightedKudosersProps,
    comments_enabled: PropTypes.bool
  }).isRequired,
  body: PropTypes.string.isRequired,
  sharedContents: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string,
      id: PropTypes.number,
      post_id: PropTypes.number,
      thumbnail_url: PropTypes.string,
      title: PropTypes.string,
      type: PropTypes.string,
      url: PropTypes.string,
      hostname: PropTypes.string
    })
  ),
  timeAndLocation: timeAndLocationProps.isRequired,
  athleteProfileId: PropTypes.string.isRequired,
  isDeletable: PropTypes.bool.isRequired,
  clubMentionsEnabled: PropTypes.bool.isRequired
};

Post.defaultProps = {
  sharedContents: []
};

export default Post;
